'use client';

import { useState, useRef, useEffect, useCallback } from 'react';
import { searchBuildings } from '@/utils/api';
import {
  advanceAddressFocus,
  assignInputRef,
} from '@/components/ui/addressSearchFocus';
import { useLanguage } from '@/context/LanguageContext';
import styles from './addressSearchInput.module.css';

export default function BuildingSearchInput({
  value = '',
  onChange,
  onBuildingSelect = null,
  streetCode = null, // Required: street code to filter buildings
  placeholder = 'Įveskite namo numerį',
  className = '',
  error = false,
  displayValue = null,
  onEnterPress = null, // Optional callback when Enter key is pressed
  nextInputRef = null,
  inputRef: externalInputRef = null,
  onActivate = null,
}) {
  const { t } = useLanguage();
  const [searchTerm, setSearchTerm] = useState(displayValue || value || '');
  const [results, setResults] = useState([]);
  const [isOpen, setIsOpen] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [selectedBuilding, setSelectedBuilding] = useState(null);
  const [customConfirmed, setCustomConfirmed] = useState(false);
  const [highlightedIndex, setHighlightedIndex] = useState(-1);
  const wrapperRef = useRef(null);
  const inputRef = useRef(null);
  const debounceTimerRef = useRef(null);
  const prevStreetCodeRef = useRef(streetCode);
  const resultsRef = useRef([]);
  resultsRef.current = results;

  // Format building display name
  const formatBuildingName = building => {
    if (building.full_number) {
      return building.full_number;
    }
    const number = building.building_number || '';
    const korpus = building.korpus || '';
    return korpus ? `${number}-${korpus}` : number;
  };

  // Initialize search term from displayValue or value
  useEffect(() => {
    if (displayValue !== null && displayValue !== undefined) {
      setSearchTerm(displayValue);
    } else if (value && !displayValue) {
      setSearchTerm(value);
    }
  }, [value, displayValue]);

  // Reset when street changes
  useEffect(() => {
    const prevStreetCode = prevStreetCodeRef.current;
    prevStreetCodeRef.current = streetCode;

    // Only reset if streetCode changed from a value to null/undefined
    if (!streetCode && prevStreetCode) {
      setSearchTerm('');
      setSelectedBuilding(null);
      setCustomConfirmed(false);
      setResults([]);
      setIsOpen(false);
      if (value) {
        onChange('');
      }
    }
  }, [streetCode, value]); // eslint-disable-line react-hooks/exhaustive-deps

  const applyCustomBuildingNumber = useCallback(
    trimmed => {
      const customBuilding = {
        code: '',
        building_number: trimmed,
        full_number: trimmed,
        isCustom: true,
      };
      setSelectedBuilding(customBuilding);
      setCustomConfirmed(true);
      setIsOpen(false);
      setResults([]);
      setHighlightedIndex(-1);
      onChange('');
      if (onBuildingSelect) {
        onBuildingSelect(customBuilding);
      }
    },
    [onChange, onBuildingSelect]
  );

  const handleConfirmCustomNumber = useCallback(() => {
    const trimmed = searchTerm?.trim();
    if (!trimmed || !streetCode) return;
    applyCustomBuildingNumber(trimmed);
    advanceAddressFocus({ nextInputRef, onEnterPress });
  }, [searchTerm, streetCode, applyCustomBuildingNumber, nextInputRef, onEnterPress]);

  // Debounced search function
  const performSearch = useCallback(
    async term => {
      if (!streetCode) {
        setResults([]);
        setIsOpen(false);
        return;
      }

      setIsLoading(true);
      try {
        const buildings = await searchBuildings(streetCode, term || null);
        setResults(buildings || []);
        setIsOpen(true);
        setHighlightedIndex(-1); // Reset highlight when new results arrive
      } catch (error) {
        console.error('[BuildingSearchInput] Search error:', error);
        setResults([]);
        setIsOpen(false);
        setHighlightedIndex(-1);
      } finally {
        setIsLoading(false);
      }
    },
    [streetCode]
  );

  // Handle input change with debounce
  const handleInputChange = e => {
    const newValue = e.target.value;
    setSearchTerm(newValue);
    setCustomConfirmed(false);
    setSelectedBuilding(null);

    // Clear previous timer
    if (debounceTimerRef.current) {
      clearTimeout(debounceTimerRef.current);
    }

    // If input is cleared, reset everything
    if (!newValue || newValue.trim() === '') {
      setResults([]);
      setIsOpen(false);
      setHighlightedIndex(-1);
      onChange('');
      if (!streetCode && onBuildingSelect) {
        onBuildingSelect({ code: '', building_number: '', full_number: '' });
      }
      return;
    }

    // Without a street, allow free-text house number entry
    if (!streetCode) {
      const trimmed = newValue.trim();
      setResults([]);
      setIsOpen(false);
      setHighlightedIndex(-1);
      onChange('');
      if (onBuildingSelect) {
        onBuildingSelect({
          code: '',
          building_number: trimmed,
          full_number: trimmed,
        });
      }
      return;
    }

    // Debounce search
    debounceTimerRef.current = setTimeout(() => {
      performSearch(newValue);
    }, 300);
  };

  // Confirm exact match or top result on blur when user did not click a suggestion
  const confirmTypedValueIfMatch = useCallback(() => {
    const trimmed = searchTerm?.trim();
    if (
      !trimmed ||
      selectedBuilding ||
      customConfirmed ||
      isLoading ||
      !streetCode
    ) {
      return;
    }

    const currentResults = resultsRef.current;
    if (!currentResults.length) return;

    const buildingToSelect =
      currentResults.find(
        b =>
          formatBuildingName(b).trim().toLowerCase() === trimmed.toLowerCase()
      ) || currentResults[0];

    setSelectedBuilding(buildingToSelect);
    setSearchTerm(formatBuildingName(buildingToSelect));
    setIsOpen(false);
    setResults([]);
    setHighlightedIndex(-1);
    onChange(buildingToSelect.code);
    if (onBuildingSelect) onBuildingSelect(buildingToSelect);
  }, [
    searchTerm,
    selectedBuilding,
    isLoading,
    streetCode,
    customConfirmed,
    onChange,
    onBuildingSelect,
  ]);

  const showCustomConfirm =
    isOpen &&
    !isLoading &&
    streetCode &&
    searchTerm.trim().length > 0 &&
    results.length === 0 &&
    !customConfirmed;

  // Handle click outside
  useEffect(() => {
    const handleClickOutside = event => {
      if (wrapperRef.current && wrapperRef.current.contains(event.target)) {
        return;
      }

      // Confirm typed value if it matches a result (before closing)
      confirmTypedValueIfMatch();

      setIsOpen(false);
      if (selectedBuilding) {
        setSearchTerm(formatBuildingName(selectedBuilding));
      } else if (!value && !searchTerm) {
        setSearchTerm('');
      }
    };

    document.addEventListener('click', handleClickOutside);
    return () => document.removeEventListener('click', handleClickOutside);
  }, [selectedBuilding, value, searchTerm, confirmTypedValueIfMatch]);

  // Handle input focus
  const handleFocus = () => {
    if (onActivate) onActivate();

    if (!streetCode) return;

    // If we have results, show them
    if (results.length > 0) {
      setIsOpen(true);
    }
    // If we have a search term but no results yet, trigger search
    else if (searchTerm && searchTerm.trim().length > 0) {
      performSearch(searchTerm);
    }
    // If no search term, show all buildings
    else {
      performSearch(null);
    }
  };

  // Handle building selection
  const handleSelectBuilding = building => {
    setSelectedBuilding(building);
    // Use formatted building number
    setSearchTerm(formatBuildingName(building));
    setIsOpen(false);
    setResults([]);
    setHighlightedIndex(-1);
    // Pass the building code to the parent
    onChange(building.code);
    // Also call onBuildingSelect if provided
    if (onBuildingSelect) {
      onBuildingSelect(building);
    }
    advanceAddressFocus({ nextInputRef, onEnterPress });
  };

  return (
    <div ref={wrapperRef} className={styles.wrapper}>
      <div
        className={`${styles.inputWrapper} ${error ? styles.inputError : ''} ${isLoading ? styles.loading : ''} ${className}`}
      >
        <input
          ref={node => assignInputRef(inputRef, externalInputRef, node)}
          type='text'
          className={styles.input}
          value={searchTerm}
          onChange={handleInputChange}
          onFocus={handleFocus}
          onBlur={() => setTimeout(confirmTypedValueIfMatch, 0)}
          onKeyDown={e => {
            if (e.key === 'Enter' && !streetCode) {
              e.preventDefault();
              advanceAddressFocus({ nextInputRef, onEnterPress });
            } else if (e.key === 'Enter' && streetCode) {
              e.preventDefault();
              if (isOpen && results.length > 0) {
                const indexToSelect =
                  highlightedIndex >= 0 ? highlightedIndex : 0;
                const buildingToSelect = results[indexToSelect];
                if (buildingToSelect) {
                  handleSelectBuilding(buildingToSelect);
                }
              } else if (!isOpen) {
                advanceAddressFocus({ nextInputRef, onEnterPress });
              }
            } else if (e.key === 'ArrowDown' && isOpen && results.length > 0) {
              e.preventDefault();
              setHighlightedIndex(prev =>
                prev < results.length - 1 ? prev + 1 : prev
              );
            }
            // Arrow up - navigate up in results
            else if (e.key === 'ArrowUp' && isOpen && results.length > 0) {
              e.preventDefault();
              setHighlightedIndex(prev => (prev > 0 ? prev - 1 : -1));
            }
            // Escape - close dropdown
            else if (e.key === 'Escape' && isOpen) {
              e.preventDefault();
              setIsOpen(false);
              setHighlightedIndex(-1);
            }
          }}
          placeholder={placeholder}
          autoComplete='off'
        />
        {isLoading && (
          <div className={styles.iconWrapper}>
            <div className={styles.spinner}></div>
          </div>
        )}
        {showCustomConfirm && (
          <div className={styles.customConfirmPanel}>
            <p className={styles.customConfirmMessage}>
              {t('calculator.step6.building_number_not_found_confirm')}
            </p>
            <button
              type='button'
              className={styles.customConfirmButton}
              onMouseDown={e => e.preventDefault()}
              onClick={handleConfirmCustomNumber}
            >
              {t('calculator.step6.confirm_building_number')}
            </button>
          </div>
        )}
      </div>

      {isOpen && results.length > 0 && (
        <ul className={styles.resultsList}>
          {results.map((building, index) => (
            <li
              key={building.code || index}
              className={`${styles.resultItem} ${
                selectedBuilding?.code === building.code ? styles.active : ''
              } ${highlightedIndex === index ? styles.highlighted : ''}`}
              onMouseDown={e => {
                e.preventDefault();
                handleSelectBuilding(building);
              }}
              onClick={() => handleSelectBuilding(building)}
              onMouseEnter={() => setHighlightedIndex(index)}
            >
              <div className={styles.resultName}>
                {formatBuildingName(building)}
              </div>
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}
